/**
 * BI P2 Consent Mode v2:租客站預設「不同意」分析/廣告儲存,同意橫幅回答後才更新。
 *
 * 為什麼手動讀寫 document.cookie 而不是 Nuxt 的 useCookie():這三個函式要能用純
 * vitest 測(見 vitest.config.ts 註解,這個專案的測試邊界只到純函式)。useCookie()
 * 依賴 Nuxt runtime context,搬進 node 環境的測試要先造一整套替身。手動操作
 * document.cookie 和既有 useRemarketing.ts 手動操作 window.dataLayer 是同一個理由。
 *
 * 為什麼是 gtag('consent', ...) 而不是自訂的 dataLayer.push({event:...}):
 * `default_consent`/`consent_update` 這種名字是我們自己取的一般事件,GTM 的 Consent
 * Mode 監聽器根本不認得,Google 端永遠收不到「使用者拒絕了」這件事——推了等於沒推。
 * Consent Mode v2 的介面是固定的:呼叫 gtag('consent','default',{...}) /
 * gtag('consent','update',{...})。這裡直接搬 Google 官方 gtag.js 初始化片段本身的寫法
 * ——`function gtag(){dataLayer.push(arguments)}`——讓 dataLayer 存的是「呼叫的引數列表」
 * 而不是我們自訂的事件物件。
 */

const CONSENT_COOKIE = 'os_consent'
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 // 一年:同意過的人不必每次進站都問
const CONSENT_DEFAULT_WAIT_MS = 500 // Google 建議值:給同意橫幅的初始判斷一點時間才套用預設值

type ConsentChoice = 'granted' | 'denied'

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function getStoredConsentChoice(): ConsentChoice | null {
  const stored = readCookie(CONSENT_COOKIE)
  return stored === 'granted' || stored === 'denied' ? stored : null
}

function writeCookie(name: string, value: string): void {
  // 隱私模式/被封鎖時 document 可能不存在或寫入被靜默忽略——不丟例外,
  // 下次進站讀不到 cookie 會被當成未回應,重新顯示橫幅,是降級不是錯誤。
  if (typeof document === 'undefined') return
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax`
}

// eslint-disable-next-line prefer-rest-params -- 刻意對齊 Google gtag.js 官方初始化片段的寫法,
// 讓 dataLayer 收到的是 arguments 物件(Consent Mode 監聽器認的形狀),不是我們自訂的一般物件。
function gtag(): void {
  // SSR 下沒有 window。少了這道守衛,元件在伺服器端跑的那一次會讓整頁 500。
  if (typeof window === 'undefined') return
  const w = window as unknown as { dataLayer?: IArguments[] }
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push(arguments)
}

function consentSignals(choice: ConsentChoice): Record<string, ConsentChoice> {
  return {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  }
}

function pushConsentUpdate(choice: ConsentChoice): void {
  gtag('consent', 'update', consentSignals(choice))
}

export function useConsent() {
  return {
    /**
     * 頁面啟動時呼叫,必須早於 GTM 腳本插入 <head>——宣告預設不同意。
     * 呼叫端見 plugins/consent.client.ts(用 enforce:'pre' 保證執行順序)。
     *
     * consentModeEnabled 來自 runtimeConfig.public.consentModeEnabled(暗旗標,
     * 預設 false)。prod 的 GTM 已經開著、帶著既有 DG 再行銷 tag 在收資料,
     * Consent Mode 不能跟著 gtm.enabled 一起打開——旗標關閉時這裡什麼都不做,
     * 不推 dataLayer、不影響既有 tag 收到的任何東西。
     */
    pushConsentDefault(consentModeEnabled: boolean): void {
      if (!consentModeEnabled) return
      gtag('consent', 'default', {
        ...consentSignals('denied'),
        wait_for_update: CONSENT_DEFAULT_WAIT_MS,
      })
    },

    /**
     * 回訪的已回應使用者:在 consent default(denied)之後,立刻用先前存的選擇
     * 補推一次 consent update,讓 GTM 用回她原本的選擇,不必每次重新問。
     * 沒回應過(cookie 不存在)就什麼都不做;旗標關閉時同樣什麼都不做。
     */
    applyStoredConsent(consentModeEnabled: boolean): void {
      if (!consentModeEnabled) return
      const stored = readCookie(CONSENT_COOKIE)
      if (stored === 'granted' || stored === 'denied') {
        pushConsentUpdate(stored)
      }
    },

    /** 使用者在同意橫幅按下「同意」。 */
    grantConsent(): void {
      writeCookie(CONSENT_COOKIE, 'granted')
      pushConsentUpdate('granted')
    },

    /** 使用者在同意橫幅按下「拒絕」。 */
    denyConsent(): void {
      writeCookie(CONSENT_COOKIE, 'denied')
      pushConsentUpdate('denied')
    },

    /** 是否已經回應過同意橫幅(同意或拒絕皆算);cookie 讀不到就視為未回應。 */
    hasRespondedToConsent(): boolean {
      return readCookie(CONSENT_COOKIE) !== null
    },

    /** 目前已存的同意選擇;沒回應過就是 null。給需要實際選擇值(而非只問有無回應)的呼叫端用。 */
    getStoredConsentChoice,

    /** 橫幅是否該顯示:旗標關閉時永遠不顯示,旗標開啟且尚未回應時才顯示。 */
    shouldShowConsentBanner(consentModeEnabled: boolean, hasResponded: boolean): boolean {
      return consentModeEnabled && !hasResponded
    },

    /**
     * stores/user.ts 的 set_user_id 是否該現在推:旗標關閉時維持既有行為,
     * 一律推(向下相容,見 nuxt.config.ts 暗旗標預設 false 的說明);旗標開啟時
     * 只有明確 granted 才推,尚未回應或已拒絕都要擋下,由呼叫端緩衝 id 等之後
     * grantConsent 或「回訪已同意」重播時再推。
     */
    shouldPushAnalyticsUserId(consentModeEnabled: boolean, consentChoice: ConsentChoice | null): boolean {
      return !consentModeEnabled || consentChoice === 'granted'
    },
  }
}
